// External libraries (alphabetically)
import { useQuery } from '@tanstack/react-query';

// Internal absolute imports (@/ aliases)
// Lib utilities
import apiClient from '@/lib/api/client';

// Constants
import { API_ENDPOINTS } from '@/constants/api';

// Relative imports from same feature
import { authKeys } from './index';

export interface UserRole {
  id: number;
  name: string;
  description?: string;
}

/**
 * DOCU: Fetches available user roles from API. <br>
 * Triggered: Called in registration form to populate role dropdown. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @returns React Query result with array of user roles.
 * @author Kirt
 */
const fetchUserRoles = async (): Promise<UserRole[]> => {
  const response = await apiClient.get<{ code: string; result: UserRole[] }>(
    API_ENDPOINTS.AUTH_ROLES // Public endpoint - no auth required
  );
  return response.data.result;
};

/**
 * DOCU: React Query hook to fetch and cache user roles. <br>
 * Triggered: Used in RegistrationForm component. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @returns Query result containing roles array, loading state, and error state.
 * @author Kirt
 */
export const useUserRoles = () => {
  return useQuery({
    queryKey: authKeys.roles(),
    queryFn: fetchUserRoles,
    staleTime: 1000 * 60 * 60, // 1 hour - roles don't change often
    retry: 2, // Retry twice on failure
  });
};
